<script setup lang="ts">
import InlineMessage from '@/components/InlineMessage.vue';
import { useAuth } from '@/composables/useAuth';
import { useFetch } from '@/composables/useFetch';
import { type MessageSchema } from '@/i18n/index.js';
import { useEnvStore } from '@/stores/envStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Password from 'primevue/password';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Props
interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  'update:visible': [value: boolean];
  'account-deleted': [];
}>();

// Composables
const { fetchData } = useFetch();
const { deleteAccount, isFetching } = useAuth();
const toast = useToast();
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

// Types
interface RestaurantDeletionInfo {
  id: string;
  name: string;
  role: string;
  is_admin_or_owner: boolean;
  is_sole_admin: boolean;
  has_employees: boolean;
  other_admins: Array<{ id: string; name: string; email: string }>;
  other_admins_count: number;
  all_members: Array<{ id: string; name: string; email: string; role: string }>;
}

interface DeletionInfo {
  restaurants: RestaurantDeletionInfo[];
  dishes_count: number;
  dish_titles_count: number;
  has_content: boolean;
}

// State
const deletePassword = ref('');
const deletePasswordError = ref('');
const deletionInfo = ref<DeletionInfo | null>(null);
const isLoadingDeletionInfo = ref(false);
const deleteDishes = ref(false);
const restaurantActions = ref<Record<string, { action: string; new_owner_id?: string }>>({});

// Computed
const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

// Watch for dialog opening
watch(
  () => props.visible,
  async (newValue) => {
    if (newValue) {
      await loadDeletionInfo();
    } else {
      resetForm();
    }
  },
);

// Methods
async function loadDeletionInfo() {
  deletePassword.value = '';
  deletePasswordError.value = '';
  deleteDishes.value = false;
  restaurantActions.value = {};

  isLoadingDeletionInfo.value = true;
  try {
    const { data, error } = await fetchData(`${useEnvStore().apiUrl}/user/account/deletion-info`, 'GET');
    if (data) {
      deletionInfo.value = data;

      // Initialize default actions for each restaurant
      data.restaurants?.forEach((restaurant: RestaurantDeletionInfo) => {
        if (restaurant.role === 'employee') {
          // Employees just leave
          restaurantActions.value[restaurant.id] = { action: 'leave' };
        } else if (restaurant.is_sole_admin && restaurant.has_employees) {
          // Sole admin with employees must choose
          restaurantActions.value[restaurant.id] = { action: 'transfer' };
        } else if (restaurant.is_sole_admin && !restaurant.has_employees) {
          // Sole admin without employees - default to delete
          restaurantActions.value[restaurant.id] = { action: 'delete' };
        } else {
          // Admin with other admins - default to leave
          restaurantActions.value[restaurant.id] = { action: 'leave' };
        }
      });
    }
    if (error) {
      console.error('Failed to fetch deletion info:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.profile.delete_account.error_fetching_info'),
        life: 3000,
      });
    }
  } catch (error) {
    console.error('Failed to fetch deletion info:', error);
  } finally {
    isLoadingDeletionInfo.value = false;
  }
}

function canProceedWithDeletion() {
  // Check if all sole admins with employees have selected a new owner
  if (!deletionInfo.value?.restaurants) return true;

  return deletionInfo.value.restaurants.every((restaurant: RestaurantDeletionInfo) => {
    const action = restaurantActions.value[restaurant.id];
    if (!action) return false;

    // If transferring, must have a new owner selected
    if (action.action === 'transfer') {
      return !!action.new_owner_id;
    }

    return true;
  });
}

async function confirmAccountDeletion() {
  deletePasswordError.value = '';

  if (!deletePassword.value) {
    deletePasswordError.value = t('profile.profile.delete_account.password_required');
    return;
  }

  if (!canProceedWithDeletion()) {
    toast.add({
      severity: 'warn',
      summary: t('common.warning'),
      detail: t('profile.profile.delete_account.must_select_new_owner'),
      life: 3000,
    });
    return;
  }

  // Build the request payload
  const restaurants = Object.entries(restaurantActions.value).map(([restaurant_id, action]) => ({
    restaurant_id,
    action: action.action,
    new_owner_id: action.new_owner_id,
  }));

  const success = await deleteAccount(deletePassword.value, deleteDishes.value, restaurants);

  if (success) {
    isVisible.value = false;
    emit('account-deleted');
  }
}

function cancelDeletion() {
  isVisible.value = false;
  resetForm();
}

function resetForm() {
  deletePassword.value = '';
  deletePasswordError.value = '';
  deletionInfo.value = null;
  restaurantActions.value = {};
  deleteDishes.value = false;
}
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :header="t('profile.profile.delete_account.warning_title')"
    :style="{ width: '50rem', maxWidth: '90vw' }"
    class="max-h-[90vh] overflow-y-auto"
  >
    <div v-if="isLoadingDeletionInfo" class="flex justify-center p-8">
      <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>

    <div v-else class="flex flex-col gap-6">
      <!-- Warning message -->
      <div class="rounded-lg border border-red-200 bg-red-50 p-4">
        <p class="mb-2 font-semibold text-red-800">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          {{ t('profile.profile.delete_account.warning_message') }}
        </p>
        <p class="text-sm text-red-700">
          {{ t('profile.profile.delete_account.permanent_action') }}
        </p>
      </div>

      <!-- What will be deleted -->
      <div class="rounded-lg border p-4">
        <h4 class="mb-3 font-semibold">{{ t('profile.profile.delete_account.will_be_deleted') }}</h4>
        <ul class="list-inside list-disc space-y-1 text-gray-700">
          <li>{{ t('profile.profile.delete_account.personal_info') }}</li>
          <li>{{ t('profile.profile.delete_account.address_info') }}</li>
          <li>{{ t('profile.profile.delete_account.account_access') }}</li>
        </ul>
      </div>

      <!-- Dishes section -->
      <div v-if="deletionInfo?.has_content" class="rounded-lg border p-4">
        <div class="flex items-start gap-3">
          <input id="delete-dishes" v-model="deleteDishes" type="checkbox" class="mt-1 cursor-pointer" />
          <div class="flex-1">
            <label for="delete-dishes" class="cursor-pointer font-semibold">
              {{ t('profile.profile.delete_account.delete_dishes_option') }}
            </label>
            <p class="mt-1 text-sm text-gray-600">
              {{
                t('profile.profile.delete_account.dishes_count', {
                  count: deletionInfo.dishes_count,
                })
              }}
            </p>
            <p class="mt-1 text-sm text-gray-500">
              {{ t('profile.profile.delete_account.dishes_kept_message') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Restaurants section -->
      <div v-if="deletionInfo?.restaurants && deletionInfo.restaurants.length > 0" class="rounded-lg border p-4">
        <h4 class="mb-4 font-semibold">{{ t('profile.profile.delete_account.restaurants_title') }}</h4>

        <div class="space-y-4">
          <div v-for="restaurant in deletionInfo.restaurants" :key="restaurant.id" class="space-y-3 rounded-lg border p-4">
            <div class="flex items-center justify-between">
              <div>
                <h5 class="font-semibold">{{ restaurant.name }}</h5>
                <p class="text-sm text-gray-600">
                  {{ t(`profile.profile.delete_account.role_${restaurant.role}`) }}
                </p>
              </div>
              <span v-if="restaurant.is_sole_admin" class="rounded bg-orange-100 px-2 py-1 text-xs text-orange-800">
                {{ t('profile.profile.delete_account.sole_admin') }}
              </span>
            </div>

            <!-- Employee: just leave -->
            <div v-if="restaurant.role === 'employee'" class="text-sm text-gray-600">
              <i class="pi pi-info-circle mr-2"></i>
              {{ t('profile.profile.delete_account.employee_will_leave') }}
            </div>

            <!-- Admin/Owner with options -->
            <div v-else class="space-y-3">
              <!-- Sole admin with employees: must transfer -->
              <div v-if="restaurant.is_sole_admin && restaurant.has_employees">
                <div class="mb-3 rounded border border-yellow-200 bg-yellow-50 p-3">
                  <p class="text-sm text-yellow-800">
                    <i class="pi pi-exclamation-triangle mr-2"></i>
                    {{ t('profile.profile.delete_account.must_transfer_ownership') }}
                  </p>
                </div>

                <div class="mb-2 flex items-center gap-2">
                  <input
                    :id="`transfer-${restaurant.id}`"
                    v-model="restaurantActions[restaurant.id].action"
                    type="radio"
                    value="transfer"
                    class="cursor-pointer"
                  />
                  <label :for="`transfer-${restaurant.id}`" class="cursor-pointer">
                    {{ t('profile.profile.delete_account.transfer_ownership') }}
                  </label>
                </div>

                <Dropdown
                  v-model="restaurantActions[restaurant.id].new_owner_id"
                  :options="restaurant.all_members"
                  option-label="name"
                  option-value="id"
                  :placeholder="t('profile.profile.delete_account.select_new_owner')"
                  class="w-full"
                >
                  <template #option="slotProps">
                    <div>
                      <div class="font-semibold">{{ slotProps.option.name }}</div>
                      <div class="text-sm text-gray-600">{{ slotProps.option.email }}</div>
                    </div>
                  </template>
                </Dropdown>

                <div class="mt-3 flex items-center gap-2">
                  <input
                    :id="`delete-${restaurant.id}`"
                    v-model="restaurantActions[restaurant.id].action"
                    type="radio"
                    value="delete"
                    class="cursor-pointer"
                    @change="restaurantActions[restaurant.id].new_owner_id = undefined"
                  />
                  <label :for="`delete-${restaurant.id}`" class="cursor-pointer text-red-600">
                    {{ t('profile.profile.delete_account.delete_restaurant') }}
                  </label>
                </div>
              </div>

              <!-- Sole admin without employees: delete or transfer -->
              <div v-else-if="restaurant.is_sole_admin && !restaurant.has_employees">
                <div class="space-y-2">
                  <div class="flex items-center gap-2">
                    <input
                      :id="`delete-${restaurant.id}`"
                      v-model="restaurantActions[restaurant.id].action"
                      type="radio"
                      value="delete"
                      class="cursor-pointer"
                      @change="restaurantActions[restaurant.id].new_owner_id = undefined"
                    />
                    <label :for="`delete-${restaurant.id}`" class="cursor-pointer text-red-600">
                      {{ t('profile.profile.delete_account.delete_restaurant') }}
                    </label>
                  </div>

                  <div v-if="restaurant.all_members.length > 0" class="flex items-center gap-2">
                    <input
                      :id="`transfer-${restaurant.id}`"
                      v-model="restaurantActions[restaurant.id].action"
                      type="radio"
                      value="transfer"
                      class="cursor-pointer"
                    />
                    <label :for="`transfer-${restaurant.id}`" class="cursor-pointer">
                      {{ t('profile.profile.delete_account.transfer_ownership') }}
                    </label>
                  </div>

                  <Dropdown
                    v-if="restaurantActions[restaurant.id].action === 'transfer'"
                    v-model="restaurantActions[restaurant.id].new_owner_id"
                    :options="restaurant.all_members"
                    option-label="name"
                    option-value="id"
                    :placeholder="t('profile.profile.delete_account.select_new_owner')"
                    class="mt-2 w-full"
                  >
                    <template #option="slotProps">
                      <div>
                        <div class="font-semibold">{{ slotProps.option.name }}</div>
                        <div class="text-sm text-gray-600">{{ slotProps.option.email }}</div>
                      </div>
                    </template>
                  </Dropdown>
                </div>
              </div>

              <!-- Admin with other admins: leave, transfer, or delete -->
              <div v-else class="space-y-2">
                <div class="flex items-center gap-2">
                  <input
                    :id="`leave-${restaurant.id}`"
                    v-model="restaurantActions[restaurant.id].action"
                    type="radio"
                    value="leave"
                    class="cursor-pointer"
                    @change="restaurantActions[restaurant.id].new_owner_id = undefined"
                  />
                  <label :for="`leave-${restaurant.id}`" class="cursor-pointer">
                    {{ t('profile.profile.delete_account.leave_restaurant') }}
                  </label>
                </div>

                <!-- <div class="flex items-center gap-2">
                  <input :id="`transfer-${restaurant.id}`" v-model="restaurantActions[restaurant.id].action"
                    type="radio" value="transfer" class="cursor-pointer" />
                  <label :for="`transfer-${restaurant.id}`" class="cursor-pointer">
                    {{ t('profile.profile.delete_account.transfer_to_specific') }}
                  </label>
                </div> -->

                <Dropdown
                  v-if="restaurantActions[restaurant.id].action === 'transfer'"
                  v-model="restaurantActions[restaurant.id].new_owner_id"
                  :options="restaurant.all_members"
                  option-label="name"
                  option-value="id"
                  :placeholder="t('profile.profile.delete_account.select_new_owner')"
                  class="mt-2 w-full"
                >
                  <template #option="slotProps">
                    <div>
                      <div class="font-semibold">{{ slotProps.option.name }}</div>
                      <div class="text-sm text-gray-600">{{ slotProps.option.email }}</div>
                    </div>
                  </template>
                </Dropdown>

                <div class="flex items-center gap-2">
                  <input
                    :id="`delete-${restaurant.id}`"
                    v-model="restaurantActions[restaurant.id].action"
                    type="radio"
                    value="delete"
                    class="cursor-pointer"
                    @change="restaurantActions[restaurant.id].new_owner_id = undefined"
                  />
                  <label :for="`delete-${restaurant.id}`" class="cursor-pointer text-red-600">
                    {{ t('profile.profile.delete_account.delete_restaurant') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Password confirmation -->
      <div class="flex flex-col gap-2">
        <label for="deletePassword" class="font-semibold">
          {{ t('profile.profile.delete_account.confirm_password_placeholder') }}
        </label>
        <Password id="deletePassword" v-model="deletePassword" class="w-full" toggle-mask :feedback="false" />
        <InlineMessage v-if="deletePasswordError" severity="error">{{ deletePasswordError }}</InlineMessage>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-end gap-3 border-t pt-4">
        <Button :label="t('profile.profile.delete_account.cancel')" severity="secondary" @click="cancelDeletion" />
        <Button
          :label="t('profile.profile.delete_account.confirm_deletion')"
          severity="danger"
          :loading="isFetching"
          :disabled="!deletePassword || !canProceedWithDeletion()"
          @click="confirmAccountDeletion"
        />
      </div>
    </div>
  </Dialog>
</template>
